import { Formik, FormikHelpers, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, Select, Input } from "formik-antd";
import { Integration } from "/app/src/models";
import { simpleSchemaBuilder } from "/app/src/helpers";

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSetting from "/app/src/components/settings/setting";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";

interface FormValues {
  value: string;
  name?: string;
}

/**
 * Component to display append lines setting for a single Data Pull Integration
 */
export default function AppendLines({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const { setting, createUpdateSetting } = useSetting({
    integrationId: integration.id,
    type: "appendLines",
  });

  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      await createUpdateSetting({
        ...values,
        type: "appendLines",
        integrationId: integration.id,
      }).then(() => {
        actions.resetForm();
      });
    },
    [createUpdateSetting, integration.id],
  );
  const valueLabelContent = useMemo(
    () => (
      <>
        {t("translation:append_lines")}
        <IconToolTip
          content={t("translation:append_lines_tip")}
          className="ml-1"
        />
      </>
    ),
    [t],
  );
  const nameLabelContent = useMemo(
    () => (
      <>
        {t("translation:name_prefix")}
        <IconToolTip
          content={t("translation:name_prefix_tip")}
          className="ml-1"
        />
      </>
    ),
    [t],
  );
  const form: (props: FormikProps<FormValues>) => JSX.Element = useCallback(
    ({ isSubmitting, dirty, values }) => (
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={values.value === "2" ? 10 : 20}>
            <Form.Item name="value" label={valueLabelContent}>
              <Select name="value" size="large">
                <Select.Option value={"0"}>
                  {t("translation:disabled")}
                </Select.Option>
                <Select.Option value={"1"}>
                  {t("translation:enabled_by_order_name")}
                </Select.Option>
                <Select.Option value={"2"}>
                  {t("translation:enabled_by_name_prefix")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          {values.value === "2" && (
            <Col span={10}>
              <Form.Item name="name" label={nameLabelContent}>
                <Input
                  size="large"
                  name="name"
                  placeholder={t("translation:enter_name_prefix_key")}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={4}>
            <SubmitButton
              type="primary"
              size="large"
              block
              disabled={!dirty || isSubmitting}
              style={{ marginTop: "30px" }}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [valueLabelContent, t, nameLabelContent],
  );
  return (
    <Formik
      initialValues={{
        value: setting?.value ? setting.value : "0",
        name: setting?.name,
      }}
      validationSchema={simpleSchemaBuilder([
        { name: "value", type: "string", required: true },
        { name: "name", type: "string", required: false },
      ])}
      onSubmit={onSubmit}
      component={form}
      enableReinitialize
    />
  );
}
